import { OrderStatus, SearchedOrder, SearchedOrderWrapper } from 'API';
import { Table } from 'components/common/Table/Table';
import { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchTableColumns } from 'shared/constants/search.constants';
import { AnalyticsEventName } from 'shared/enums/analytics';
import { getOrderDetailPath } from 'shared/helpers/route.helper';
import { SearchedOrderMinimum } from 'shared/models';
import { AnalyticsService } from 'shared/services/analytics.service';
import { getSearchScreenDateInShortFormat } from 'shared/utils';
import ExpandableOrders from './ExpandableOrders';

/**
 * Input props for {@link SearchTable}
 */
export interface SearchTableProps {
  /**
   * page number for pagination
   */
  page: number;

  /**
   * call back to indicate change in page selected
   * @param page - selected page number
   */
  setPage?: (page: number) => void;
  /**
   * sort by column state
   */
  sortByColumn?: string;
  /**
   * call back to indicate sort by column state
   * @param column - selected column
   */
  setSortByColumn?: (column: string) => void;
  /**
   * limit of items to be displayed on the page
   */
  limit: number;
  /**
   * call back to indicate change in limit of items to be displayed
   * @param limit - selected limit
   */
  setLimit: (limit: number) => void;
  /**
   * data to be displayed in the table
   */
  data?: SearchedOrderWrapper | null;
  /**
   * loading state of the table
   */
  loading?: boolean;
  /**
   * search state containing the search parameters
   */
  searchState: {
    caseNumber: string;
    patientFirstName: string;
    patientLastName: string;
    providerName: string;
    accountNumber: string;
    statuses: OrderStatus[];
  };
  /**
   * show pagination in the table
   */
  showPagination?: boolean;
}

/**
 * Renders a search results table
 *
 * @param page - page number for pagination
 * @param setPage - call back to indicate change in page selected
 * @param setSortByColumn - call back to indicate sort by column state
 */
export const SearchTable: FC<SearchTableProps> = ({
  data,
  loading,
  limit,
  setLimit,
  page,
  setPage,
  sortByColumn,
  setSortByColumn,
  searchState,
  showPagination = true,
}) => {
  const navigate = useNavigate();

  /**
   * Navigates to the requested order number's case details page, with the search value saved.
   * @param orderNumber - The target order number to navigate to.
   */
  const navigateToOrderDetails = useCallback(
    (orderNumber: string) => {
      AnalyticsService.track(AnalyticsEventName.SearchResultSelected, {
        caseNumber: orderNumber,
        searchState,
      });
      navigate(
        {
          pathname: getOrderDetailPath(orderNumber),
        },
        {
          state: searchState,
        }
      );
    },
    [navigate, searchState]
  );

  const rows = useMemo((): SearchedOrderMinimum[] => {
    if (!data) return [];

    const mapOrderFn = (order: SearchedOrder) => {
      // build product description and attributes elements
      const description = <ExpandableOrders order={order} />;
      const labReceivedDate = order.labReceivedDate || order.createdDate;

      // map values from the order to the table data
      return {
        orderNumber: order.orderNumber,
        patientFirstName: order.patientFirstName || '',
        patientLastName: order.patientLastName || '',
        productDescription: description,
        billingAccountId: order.billingAccountId,
        providerId: order.providerId,
        status: order.status,
        providerName: order.providerName,
        originatingSystem: order.originatingSystem,
        labReceivedDate: labReceivedDate
          ? getSearchScreenDateInShortFormat(labReceivedDate, order.utcConversionTimeZoneCode)
          : 'N/A',
        invoiceDate: order.invoiceDate
          ? getSearchScreenDateInShortFormat(order.invoiceDate, order.utcConversionTimeZoneCode)
          : 'N/A',
      };
    };

    return data.orders.map(mapOrderFn);
  }, [data]);

  return (
    <>
      <Table
        columns={searchTableColumns(navigateToOrderDetails)}
        rows={rows}
        loading={loading}
        page={page}
        setPage={setPage}
        pageSize={limit}
        setPageSize={setLimit}
        showNewPageSizeOption
        orderByColumn={sortByColumn}
        setOrderByColumn={setSortByColumn}
        disableLocalSort
        showPagination={showPagination}
        totalCount={data?.total}
        isApiPaginated
        borderRounded
        overflowHidden
      />
    </>
  );
};
